import React, { Suspense, useState } from 'react';
import tw from 'twin.macro';
import CardMain from '$/components/Interactions/cards/CardMain';
import { useCardContext } from '$/components/Interactions/cards/CardContext';
import { css } from '@emotion/react';
import { useFormContext } from 'react-hook-form';
import { InteractionDisplayState } from '../InteractionInfo';
import { theme } from '$/components/Layout/theme';
import { OptionField, ResultsAppearAt, SelectionType } from '$/GraphQL/schema/schema';
import RadioInput from '$/components/ui/Input/Basics/RadioInput';
import { Trans, t } from '@lingui/macro';
import { withWebinarPlayerContext } from '$/components/WebinarPlayer/context';
import Checkbox from '$/components/ui/Input/Basics/Checkbox';
import Form from '$/components/ui/Form';
import Input from '$/components/ui/Input/Basics/Input';

const AnalyticsPollCardBody = React.lazy(() => import('./AnalyticsPollCard'));

const emptyAnswersArray: OptionField[] = [];

const PollCard: React.FC = withWebinarPlayerContext(['markInteractionActive'])(({ context }) => {
  const { interaction, displayState } = useCardContext();
  const interactionReactionResults = interaction.useInteractionReactionResults(
    displayState !== InteractionDisplayState.Result
  );

  const { setValue } = useFormContext();
  const { markInteractionActive } = context;

  const details = interaction.details;
  const answers = details?.answers ?? emptyAnswersArray;

  const answersMap = React.useMemo(
    () =>
      answers?.reduce<{ [map: string]: number }>((map, answer, index) => {
        const answerValue = answer.value || answer.text;
        map[answerValue] = index;
        return map;
      }, {}) || {},
    [answers]
  );

  const [isDisabled, setIsDisabled] = useState(true);
  const [arrayAnswer, setArrayAnswer] = useState<string[]>([]);

  const onChangeRadio = React.useCallback(
    (a: string): void => {
      const index = answersMap[a];

      setArrayAnswer([a]);
      setValue('arrayAnswer', [a], { shouldDirty: true });
      setIsDisabled(index === undefined);

      markInteractionActive(interaction);
    },
    [answersMap, interaction, markInteractionActive, setValue]
  );

  const onMultiChange = (answer: string, selected?: boolean): void => {
    setValue(`arrayAnswer`, undefined, { shouldDirty: true });
    if (typeof selected === 'undefined') {
      selected = !arrayAnswer.find((a) => a === answer);
    }
    let newAnswers = [];
    if (selected) {
      newAnswers = [...new Set([...arrayAnswer, answer])];
    } else {
      newAnswers = arrayAnswer.filter((a) => a !== answer);
    }
    setArrayAnswer(newAnswers);
    setValue(`arrayAnswer`, newAnswers, { shouldDirty: true });
    setIsDisabled(newAnswers.length === 0);
  };

  switch (displayState) {
    case InteractionDisplayState.Submitted:
      return (
        <>
          <CardMain.SubmitBubble>
            <CardMain.IconThanks />
            <CardMain.Jumbotron>
              <Trans>Thanks!</Trans>
            </CardMain.Jumbotron>
            {interaction.details?.resultsAppearAt === ResultsAppearAt.EndOfPresentation && (
              <CardMain.Footer>
                <Trans>
                  The results of the poll will be shared at the end of the presentation.
                </Trans>
              </CardMain.Footer>
            )}
          </CardMain.SubmitBubble>
        </>
      );

    case InteractionDisplayState.Result:
      if (!interactionReactionResults) {
        return null;
      }
      return (
        <>
          <Suspense>
            <AnalyticsPollCardBody
              results={{
                forAnalytics: false,
                ...interactionReactionResults,
              }}
              interaction={interaction}
            />
          </Suspense>
          <CardMain.Footer>
            <span className='text-2xl mr-1/2e'>☝️</span> <Trans>Here are the results!</Trans>
          </CardMain.Footer>
        </>
      );

    case InteractionDisplayState.Normal:
      return (
        <>
          {details?.selectionType === SelectionType.Multi ? (
            <div
              css={css`
                margin-bottom: 1rem;
                color: rgba(0, 0, 0, 0.609);
              `}
            >
              {t`Check all that apply.`}
            </div>
          ) : null}
          <div>
            <Form.Field
              name='details.selectionType'
              defaultValue={details?.selectionType || SelectionType.Single}
              type='hidden'
              className='hidden'
              component={<Input />}
            />
            <Form.Field
              name={`arrayAnswer`}
              type='hidden'
              className='hidden'
              component={<Input />}
            />
            {/* this field is used to validate Other answer in AttendeeCard.tsx */}
            <Form.Field
              name={`otherName`}
              defaultValue={answers?.find((a) => a.isOther === true)?.text}
              type='hidden'
              className='hidden'
              component={<Input />}
            />
            {answers?.map((answer, idx) => {
              const answerValue = answer.value || answer.text;
              const answerChecked = arrayAnswer.find((a) => a === answerValue) ? true : false;
              return (
                <div
                  key={`${answerValue}-${idx}`}
                  onClick={(): void => {
                    if (details?.selectionType === SelectionType.Multi) {
                      onMultiChange(answerValue);
                    } else {
                      onChangeRadio(answerValue);
                    }
                  }}
                  className={`${
                    arrayAnswer?.includes(answerValue) ? 'opacity-100' : 'opacity-70'
                  } flex p-2 bg-blue-1 rounded-2xl items-center mb-4 leading-tight`}
                >
                  {details?.selectionType === SelectionType.Multi ? (
                    <div className='flex flex-col w-full'>
                      <Checkbox
                        checked={arrayAnswer?.includes(answerValue)}
                        label={
                          <label
                            className='label-text text-sm w-full'
                            css={css`
                              ${tw`ml-2`}
                              word-break: break-word;
                              padding-right: 0.5rem;

                              &.label-text:before {
                                border-radius: 50% !important;
                              }
                              &.label-text:after {
                                background-color: ${interaction.ewebinar?.primaryColor ||
                                theme.colors.color.brand} !important;
                                color: #fff !important;
                                border-radius: 50% !important;
                                font-size: 12px !important;
                              }
                            `}
                          >
                            {answer.text}
                          </label>
                        }
                        onCheckBoxChanged={(_e, val): void => {
                          onMultiChange(answerValue, val);
                        }}
                      />
                      {answer.isOther && answerChecked && (
                        <Form.Field
                          className='w-full my-2'
                          name='stringAnswer'
                          component={
                            <Input
                              placeholder={t`Please specify`}
                              onClick={(e): void => e.stopPropagation()}
                            />
                          }
                        />
                      )}
                    </div>
                  ) : (
                    <div className='flex flex-col w-full'>
                      <RadioInput
                        name='details.radiovalue'
                        checked={arrayAnswer[0] === answerValue}
                        value={answerValue}
                        label={
                          <label
                            className='text-sm w-full'
                            css={css`
                              ${tw`ml-2`}
                              word-break: break-word;
                              padding-right: 0.5rem;

                              &:after {
                                background-color: ${interaction.ewebinar?.primaryColor ||
                                theme.colors.color.brand} !important;
                              }
                            `}
                          >
                            {answer.text}
                          </label>
                        }
                        onChange={onChangeRadio}
                      />
                      {answer.isOther && answerChecked && (
                        <Form.Field
                          className='w-full my-2'
                          name='stringAnswer'
                          component={
                            <Input
                              disabled={arrayAnswer[0] === answerValue ? false : true}
                              placeholder={t`Please specify`}
                              onClick={(e): void => e.stopPropagation()}
                            />
                          }
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <CardMain.ExpiresButton
            disabled={isDisabled}
            interaction={interaction}
            expiredText={''}
            buttonText={details?.buttonText || t`Submit`}
          />
        </>
      );
  }
});

export default PollCard;
